import { FloraButton, Flex } from '@grupoboticario/flora-react'
import { styled } from '@grupoboticario/flora-react-system'

export const StyledAlert = styled('div', {
  fontSize: '$bodyLarge',
  fontWeight: '$medium',
  border: '1px solid transparent',

  borderRadius: '8px',
  padding: '$3',
  marginBottom: '$4',
  display: 'flex',
  alignItems: 'center',

  variants: {
    status: {
      success: {
        backgroundColor: '$statusSuccessBackgroundDefault',
        color: '$statusSuccessNonInteractiveEmphasis',
        border: '1px solid $statusSuccessNonInteractiveEmphasis',
      },
      error: {
        backgroundColor: '$statusErrorBackgroundDefault',
        color: '$statusErrorNonInteractiveEmphasis',
        border: '1px solid $statusErrorNonInteractiveEmphasis',
      },
      warning: {
        backgroundColor: '$statusAlertBackgroundDefault',
        color: '$statusAlertNonInteractiveEmphasis',
        border: '1px solid $statusAlertNonInteractiveEmphasis',
      },
      info: {
        backgroundColor: '$statusInfoBackgroundDefault',
        color: '$statusInfoNonInteractiveEmphasis',
        border: '1px solid $statusInfoNonInteractiveEmphasis',
      },
    },
    color: {
      light: {},
      dark: {},
    },
    size: {
      default: {
        flexDirection: 'column',
      },
      small: {
        flexDirection: 'row',
      },
    },
  },

  compoundVariants: [
    {
      status: 'success',
      color: 'dark',
      css: {
        backgroundColor: '$statusSuccessBackgroundAlt',
        color: '$statusSuccessNonInteractiveAltEmphasis',
        border: '1px solid $statusSuccessNonInteractiveAltEmphasis',
      },
    },
    {
      status: 'error',
      color: 'dark',
      css: {
        backgroundColor: '$statusErrorBackgroundAlt',
        color: '$statusErrorNonInteractiveAltEmphasis',
        border: '1px solid $statusErrorNonInteractiveAltEmphasis',
      },
    },
    {
      status: 'warning',
      color: 'dark',
      css: {
        backgroundColor: '$statusAlertBackgroundAlt',
        color: '$statusAlertNonInteractiveAltEmphasis',
        border: '1px solid $statusAlertNonInteractiveAltEmphasis',
      },
    },
    {
      status: 'info',
      color: 'dark',
      css: {
        backgroundColor: '$statusInfoBackgroundAlt',
        color: '$statusInfoNonInteractiveAltEmphasis',
        border: '1px solid $statusInfoNonInteractiveAltEmphasis',
      },
    },
  ],

  defaultVariants: {
    status: 'success',
    color: 'light',
    size: 'default',
  },
})

export const StyledFlex = styled('div', {
  minHeight: '$sizes$8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',

  variants: {
    size: {
      default: {
        width: '100%',
      },
      small: {},
    },
  },
})

export const StyledDescription = styled('div', {
  width: '100%',
  fontSize: '$bodySmall',
  fontWeight: '$regular',
  marginLeft: '4px',
})

export const StyledButton = styled('button', {
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  border: 'none',
  '&:hover': {
    cursor: 'pointer',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
  },

  variants: {
    status: {
      success: {
        color: '$statusSuccessNonInteractiveEmphasis',
        '&:hover': {
          backgroundColor: '$statusSuccessNonPrimaryButtonHover',
        },
      },
      error: {
        color: '$statusErrorNonInteractiveEmphasis',
        '&:hover': {
          backgroundColor: '$statusErrorNonPrimaryButtonHover',
        },
      },
      warning: {
        color: '$statusAlertNonInteractiveEmphasis',
        '&:hover': {
          backgroundColor: '$statusAlertNonPrimaryButtonHover',
        },
      },
      info: {
        color: '$statusInfoNonInteractiveEmphasis',
        '&:hover': {
          backgroundColor: '$statusInfoNonPrimaryButtonHover',
        },
      },
    },
    color: {
      light: {},
      dark: {},
    },
  },

  compoundVariants: [
    {
      status: 'success',
      color: 'dark',
      css: {
        color: '$statusSuccessNonInteractiveAltEmphasis',
        '&:hover': {
          backgroundColor: '$statusSuccessNonPrimaryButtonAltHover',
        },
      },
    },
    {
      status: 'error',
      color: 'dark',
      css: {
        color: '$statusErrorNonInteractiveAltEmphasis',
        '&:hover': {
          backgroundColor: '$statusErrorNonPrimaryButtonAltHover',
        },
      },
    },
    {
      status: 'warning',
      color: 'dark',
      css: {
        color: '$statusAlertNonInteractiveAltEmphasis',
        '&:hover': {
          backgroundColor: '$statusAlertNonPrimaryButtonAltHover',
        },
      },
    },
    {
      status: 'info',
      color: 'dark',
      css: {
        color: '$statusInfoNonInteractiveAltEmphasis',
        '&:hover': {
          backgroundColor: '$statusInfoNonPrimaryButtonAltHover',
        },
      },
    },
  ],
})

export const StyledActionsContainer = styled(Flex, {
  justifyContent: 'flex-start',

  variants: {
    size: {
      default: {
        mt: '$2',
        width: '100%',
      },
      small: {},
    },
  },
})

export const StyledActionButton = styled(FloraButton, {
  backgroundColor: 'transparent',

  variants: {
    status: {
      success: {},
      warning: {},
      info: {},
      error: {},
    },
    color: {
      light: {},
      dark: {},
    },
    hierarchyVariant: {
      primary: {},
      secondary: {},
      tertiary: {},
    },
  },

  compoundVariants: [
    // Info Light
    {
      status: 'info',
      hierarchyVariant: 'primary',
      color: 'light',
      css: {
        backgroundColor: '$statusInfoActionableDefault',
        color: '$statusInfoActionableContent',
        '&:hover': {
          backgroundColor: '$statusInfoActionableHover',
        },
      },
    },

    {
      status: 'info',
      hierarchyVariant: 'secondary',
      color: 'light',
      css: {
        borderColor: '$statusInfoActionableDefault',
        color: '$statusInfoNonPrimaryButtonContent',

        '&:hover': {
          borderColor: '$statusInfoNonPrimaryButtonStroke',
          backgroundColor: '$statusInfoNonPrimaryButtonHover',
        },
      },
    },

    {
      status: 'info',
      hierarchyVariant: 'tertiary',
      color: 'light',
      css: {
        color: '$statusInfoNonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$statusInfoNonPrimaryButtonHover',
        },
      },
    },

    // Info Dark
    {
      status: 'info',
      hierarchyVariant: 'primary',
      color: 'dark',
      css: {
        backgroundColor: '$statusInfoActionableAlt',

        '&:hover': {
          backgroundColor: '$statusInfoActionableAltHover',
        },
      },
    },

    {
      status: 'info',
      hierarchyVariant: 'secondary',
      color: 'dark',
      css: {
        borderColor: '$statusInfoActionableAltActive',
        color: '$statusInfoActionableAltActive',

        '&:hover': {
          color: '$statusInfoActionableAltContent',
          backgroundColor: '$statusInfoActionableAltHover',
          borderColor: '$statusInfoActionableAltHover',
        },
      },
    },

    {
      status: 'info',
      hierarchyVariant: 'tertiary',
      color: 'dark',
      css: {
        color: '$statusInfoActionableAltActive',

        '&:hover': {
          color: '$statusInfoActionableAltContent',
          backgroundColor: '$statusInfoActionableAltHover',
        },
      },
    },

    // Error Light
    {
      status: 'error',
      hierarchyVariant: 'primary',
      color: 'light',
      css: {
        backgroundColor: '$statusErrorActionableHover',
        '&:hover': {
          backgroundColor: '$statusErrorActionableActive',
        },
      },
    },

    {
      status: 'error',
      hierarchyVariant: 'secondary',
      color: 'light',
      css: {
        borderColor: '$statusErrorActionableHover',
        color: '$statusErrorActionableHover',
        '&:hover': {
          color: '$statusErrorNonPrimaryButtonContent',
          borderColor: '$statusErrorNonPrimaryButtonStroke',
        },
      },
    },
  ],

  defaultVariants: {
    hierarchyVariant: 'primary',
  },
})
